// Signal-backed store for the notification bell.
// Polls the API every 30s while a session is open; manual refresh + mark-read
// helpers update the signal optimistically.

import { signal, type Signal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import type { NotificationItem, UserPreferences, UserPreferencesUpdate } from "@juuplan/shared";
import { api, ApiError } from "../lib/api";
import { refreshInboxCount } from "../lib/inboxBadge";

// After a bell mutation, keep the unified inbox badge + the Inbox page (if open)
// in sync. The read endpoints mirror server-side into inbox_state; this just
// re-pulls the derived count and nudges the Inbox list to re-fetch.
function syncInboxAfterBellMutation(): void {
  refreshInboxCount();
  window.dispatchEvent(new Event("juuplan:inbox-refresh"));
}

export interface NotificationsState {
  items: NotificationItem[];
  unread: number;
  loading: boolean;
}

const initial: NotificationsState = { items: [], unread: 0, loading: true };
const notifSignal: Signal<NotificationsState> = signal(initial);

let pollTimer: ReturnType<typeof setInterval> | null = null;
let started = false;
// Mounted consumers of useNotifications — polling stops when the last unmounts
// (prevents a leaked interval / double timers on HMR remounts).
let subscribers = 0;

async function load(): Promise<void> {
  try {
    const data = await api<{ items: NotificationItem[]; unread: number }>(
      "/api/notifications",
      { silent401: true, silentError: true }
    );
    notifSignal.value = { items: data.items, unread: data.unread, loading: false };
    // Keep the unified badge on the same 30s heartbeat as the bell list (the
    // badge now reads the inbox count, so it needs its own refresh here too).
    refreshInboxCount();
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return;
    notifSignal.value = { ...notifSignal.value, loading: false };
  }
}

export function useNotifications(): NotificationsState {
  useEffect(() => {
    subscribers += 1;
    if (!started) {
      started = true;
      void load();
      pollTimer = setInterval(() => {
        void load();
      }, 30_000);
    }
    return () => {
      subscribers -= 1;
      if (subscribers <= 0) stopNotificationPolling();
    };
  }, []);
  return notifSignal.value;
}

export function refreshNotifications(): Promise<void> {
  return load();
}

export async function markRead(id: number): Promise<void> {
  await api(`/api/notifications/${id}/read`, { method: "POST", silentError: true });
  // Decrement only if the item is still unread locally — the 30s poll may have
  // refreshed during the await and already counted it, and a blind -1 would
  // then double-decrement.
  const prev = notifSignal.value;
  const wasUnread = prev.items.some((n) => n.id === id && !n.read_at);
  notifSignal.value = {
    ...prev,
    items: prev.items.map((n) =>
      n.id === id ? { ...n, read_at: n.read_at ?? new Date().toISOString() } : n
    ),
    unread: Math.max(0, prev.unread - (wasUnread ? 1 : 0)),
  };
  syncInboxAfterBellMutation();
}

export async function markAllRead(): Promise<void> {
  await api(`/api/notifications/read-all`, { method: "POST", silentError: true });
  const now = new Date().toISOString();
  notifSignal.value = {
    ...notifSignal.value,
    items: notifSignal.value.items.map((n) =>
      n.read_at ? n : { ...n, read_at: now }
    ),
    unread: 0,
  };
  syncInboxAfterBellMutation();
}

export async function fetchPreferences(): Promise<UserPreferences> {
  return api<UserPreferences>("/api/me/preferences");
}

export async function updatePreferences(
  patch: UserPreferencesUpdate
): Promise<UserPreferences> {
  return api<UserPreferences>("/api/me/preferences", {
    method: "PATCH",
    body: patch,
  });
}

export function stopNotificationPolling(): void {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  started = false;
}
